import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { Badge, CbhIcon } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useGetEasySchedulingPlacementApplications } from "../api/useGetEasySchedulingPlacementApplications";
import { useGetEasySchedulingPlacements } from "../api/useGetEasySchedulingPlacements";

interface BadgedJobsIconProps {
  /**
   * Icon type to display
   */
  type: "briefcase" | "briefcase-colored" | "rebrand-briefcase-colored";
  /**
   * Icon size
   */
  size: "large" | "medium" | "small";
}

/**
 * Displays a briefcase icon with a red badge showing the count of easy scheduling placements.
 * Automatically fetches the count and shows/hides the badge based on availability.
 *
 * @example
 * ```tsx
 * // In navigation
 * <BadgedJobsIcon type="briefcase-colored" size="large" />
 *
 * // Inactive state
 * <BadgedJobsIcon type="briefcase" size="large" />
 * ```
 */
export function BadgedJobsIcon({ type, size }: BadgedJobsIconProps) {
  const worker = useDefinedWorker();
  const { data: placementCandidate } = useCreateOrGetPlacementCandidate({
    workerId: worker.userId,
  });

  const isPlacementApplicationsMigrationEnabled = useCbhFlag(
    CbhFeatureFlag.PLACEMENT_APPLICATIONS_MIGRATION,
    { defaultValue: false }
  );

  const isLegacyQueryEnabled =
    !isPlacementApplicationsMigrationEnabled &&
    isDefined(worker.userId) &&
    isDefined(placementCandidate?.id);
  const isPlacementApplicationsQueryEnabled =
    isPlacementApplicationsMigrationEnabled && isDefined(worker.userId);

  // Fetch easy scheduling placements - uses client-side counting from first page (200 items)
  const { data: legacyEasySchedulingData } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      shouldLogEvent: false,
    },
    {
      enabled: isLegacyQueryEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Count accepted applications eligible for easy scheduling. The new endpoint does not accept
  // filter[applicationMethod] server-side, so we filter on meta.isEasyScheduling (backend-computed:
  // scheduleInterviews=false + availability configured + feature flag enabled) client-side.
  // Flattening across all loaded pages lets the count reflect pagination driven by
  // AppliedPlacementListView (same query key).
  const { data: easySchedulingApplicationsData } = useGetEasySchedulingPlacementApplications(
    {
      workerId: worker.userId,
    },
    {
      enabled: isPlacementApplicationsQueryEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const easySchedulingCount = isPlacementApplicationsMigrationEnabled
    ? (easySchedulingApplicationsData?.pages
        .flatMap((page) => page.data)
        .filter((application) => application.meta.isEasyScheduling).length ?? 0)
    : (legacyEasySchedulingData?.pages[0]?.data.length ?? 0);
  const hasEasySchedulingJobs = easySchedulingCount > 0;

  return (
    <Badge count={easySchedulingCount} color="error" invisible={!hasEasySchedulingJobs}>
      <CbhIcon type={type} size={size} />
    </Badge>
  );
}
